import { statusColor, priorityColor } from '../../lib/utils.ts';
import { TICKET_STATUS_LABELS, TICKET_QUEUE_LABELS } from '@runninglime/shared';
import type { TicketStatus as TStatus, TicketQueue as TQueue } from '@runninglime/shared';

export function StatusBadge({ status }: { status: string }) {
  const label = (TICKET_STATUS_LABELS as Record<string, string>)[status] || status.replace(/-/g, ' ');
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor(status)}`}>
      {label}
    </span>
  );
}

const QUEUE_COLORS: Record<string, string> = {
  general: 'bg-gray-100 text-gray-700',
  billing: 'bg-violet-100 text-violet-700',
  shipping: 'bg-sky-100 text-sky-700',
  'product-issue': 'bg-rose-100 text-rose-700',
  returns: 'bg-amber-100 text-amber-700',
  technical: 'bg-indigo-100 text-indigo-700',
};

export function QueueBadge({ queue }: { queue: string }) {
  const label = (TICKET_QUEUE_LABELS as Record<string, string>)[queue] || queue;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${QUEUE_COLORS[queue] || 'bg-gray-100 text-gray-700'}`}>
      {label}
    </span>
  );
}

export function PriorityBadge({ priority }: { priority: string }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityColor(priority)}`}>
      {priority === 'urgent' && (
        <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
      )}
      {priority}
    </span>
  );
}

export function ProductPlaceholder({ name, category, imageUrl }: { name: string; category: string; imageUrl?: string }) {
  const gradients: Record<string, string> = {
    soda: 'from-lime/60 to-green/60',
    energy: 'from-accent/60 to-lime/60',
    brainstorm: 'from-green/60 to-dark-light/60',
    hydrate: 'from-blue-400/60 to-green/60',
    'cold-brew': 'from-amber-700/60 to-dark/60',
  };

  if (imageUrl) {
    const API_BASE = import.meta.env.VITE_API_BASE || '/api';
    const src = imageUrl.startsWith('http') ? imageUrl : `${API_BASE.replace(/\/api\/?$/, '')}${imageUrl}`;
    return (
      <div className="aspect-square rounded-xl overflow-hidden bg-gray-100 img-zoom">
        <img src={src} alt={name} className="w-full h-full object-cover" />
      </div>
    );
  }

  return (
    <div className={`aspect-square rounded-xl bg-gradient-to-br ${gradients[category] || 'from-lime/60 to-green/60'} flex items-center justify-center p-6 transition-all duration-300`}>
      <div className="text-center">
        <div className="text-3xl mb-2 animate-gentle-pulse">🍋</div>
        <p className="text-sm font-medium text-dark/70 line-clamp-2">{name}</p>
      </div>
    </div>
  );
}

export function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-10 w-10 border-2 border-lime border-t-transparent" />
    </div>
  );
}

export function EmptyState({ icon, title, description, action }: {
  icon: string;
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="text-center py-16 animate-fade-in">
      <div className="text-5xl mb-4 animate-float">{icon}</div>
      <h3 className="text-lg font-semibold text-dark mb-2">{title}</h3>
      <p className="text-gray-500 mb-6 max-w-md mx-auto">{description}</p>
      {action}
    </div>
  );
}
